import { Col, Container, Row } from "react-bootstrap";
import { useEffect, useState } from "react";
import { axiosPrivate } from "../api/axios";
import { CircularProgress, FormControl, TextField, Button, Pagination } from "@mui/material";
import { Link } from "react-router-dom";
import { formatCurrency } from "../functions/formatCurrency";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "../assets/styles/pages/_orders.scss";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function PaginatedOrders() {
  const [isLoading, setIsLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 5,
    totalItems: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [currentPage, setCurrentPage] = useState(1);

  const fetchOrders = async (page = 1, searchQuery = '') => {
    setIsLoading(true);

    try {
      const params = {
        pageNumber: page,
      };

      // Only add search query if it's 3+ characters
      if (searchQuery && searchQuery.trim().length >= 3) {
        params.q = searchQuery.trim();
      }

      const response = await axiosPrivate.get('/api/exo/orders/paginated', { params });

      setOrders(response.data.orders || []);
      setPagination(response.data.pagination || {
        currentPage: 1,
        pageSize: pageSize,
        totalItems: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false
      });

    } catch (error) {
      console.error('Error fetching orders:', error);
      if (error.response) {
        console.log(error.response.data);
      }
      // Reset to empty state on error
      setOrders([]);
      setPagination({
        currentPage: 1,
        pageSize: pageSize,
        totalItems: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event && event.preventDefault();

    if (query.trim().length > 0 && query.trim().length < 3) {
      return; // Don't search with less than 3 characters
    }

    setCurrentPage(1); // Reset to first page when searching
    await fetchOrders(1, query);
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    fetchOrders(page, query);
  };

  useEffect(() => {
    fetchOrders(currentPage, query);
  }, []); // Only run on component mount

  const getStatusBadge = (status) => {
    const statusMap = {
      '1': { text: "Partly Processed", class: "status-partly" },
      '2': { text: "Fully Processed", class: "status-fully" }
    };

    return statusMap[status] || { text: "Pending", class: "status-pending" };
  };

  // Calculate rebate amount for an order
  const calculateOrderRebate = (order) => {
    if (!order.LINES) return 0;

    const stcItems = order.LINES.filter(line => line.STOCKCODE === 'WS-H-STC-ASSIGNED');
    return stcItems.reduce((total, item) => {
      return total + (Math.floor(item.ORDQUANT) * 35); // $35 per STC certificate
    }, 0);
  };

  return (
    <Container>
      {/* ...existing header code... */}

      {/* Desktop Table View */}
      <table className="order-index">
        <thead>
          <tr className="text-black">
            <th>Order #</th>
            <th>Date</th>
            <th>YourRef</th>
            <th>Address</th>
            <th className="text-end">Order Total</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan="7" className="text-center">
                <CircularProgress />
              </td>
            </tr>
          ) : orders?.length ? (
            orders.map((order) => {
              const statusInfo = getStatusBadge(order.STATUS);
              const rebateAmount = calculateOrderRebate(order);
              const finalTotal = order.ORDTOTAL - rebateAmount;

              return (
                <tr key={order.SEQNO}>
                  <td>{order.SEQNO}</td>
                  <td>{dayjs(order.ORDERDATE).format('D MMMM YYYY')}</td>
                  <td>{order.CUSTORDERNO}</td>
                  <td>
                    {[order.ADDRESS1, order.ADDRESS2, order.ADDRESS3, order.ADDRESS4, order.ADDRESS5]
                      .filter(Boolean)
                      .join(' ')
                    }
                  </td>
                  <td className="text-end">
                    {rebateAmount > 0 ? (
                      <div>
                        <div className="text-muted text-decoration-line-through small">
                          {formatCurrency(order.ORDTOTAL)}
                        </div>
                        <div className="fw-bold text-success">
                          {formatCurrency(finalTotal)}
                        </div>
                        <small className="text-muted">
                          (After {formatCurrency(rebateAmount)} STC rebate)
                        </small>
                      </div>
                    ) : (
                      formatCurrency(order.ORDTOTAL)
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${statusInfo.class}`}>
                      {statusInfo.text}
                    </span>
                  </td>
                  <td className="text-center">
                    <Link to={"/order/" + order.SEQNO} className="action view">
                      View
                    </Link>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="7" className="text-center">
                {query ? `No orders found for "${query}"` : "No order records found"}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Mobile Card View */}
      <div className="mobile-cards">
        {isLoading ? (
          <div className="loading-card">
            <CircularProgress />
            <p>Loading orders...</p>
          </div>
        ) : orders?.length ? (
          orders.map((order) => {
            const statusInfo = getStatusBadge(order.STATUS);
            const rebateAmount = calculateOrderRebate(order);
            const finalTotal = order.ORDTOTAL - rebateAmount;
            const fullAddress = [order.ADDRESS1, order.ADDRESS2, order.ADDRESS3, order.ADDRESS4, order.ADDRESS5]
              .filter(Boolean)
              .join(' ');

            return (
              <div key={order.SEQNO} className="order-card">
                <div className="card-header">
                  <span className="order-number">Order #{order.SEQNO}</span>
                  <span className={`status-badge ${statusInfo.class}`}>
                    {statusInfo.text}
                  </span>
                </div>

                <div className="card-body">
                  <div className="card-field">
                    <span className="field-label">Date:</span>
                    <span className="field-value">
                      {dayjs(order.ORDERDATE).format('D MMMM YYYY')}
                    </span>
                  </div>

                  <div className="card-field">
                    <span className="field-label">Your Ref:</span>
                    <span className="field-value">{order.CUSTORDERNO || 'N/A'}</span>
                  </div>

                  <div className="card-field">
                    <span className="field-label">Address:</span>
                    <span className="field-value">{fullAddress || 'N/A'}</span>
                  </div>

                  <div className="card-field">
                    <span className="field-label">Total:</span>
                    <span className="field-value">
                      {rebateAmount > 0 ? (
                        <div>
                          <div className="text-muted text-decoration-line-through small">
                            {formatCurrency(order.ORDTOTAL)}
                          </div>
                          <div className="fw-bold text-success total-amount">
                            {formatCurrency(finalTotal)}
                          </div>
                          <small className="text-muted d-block">
                            (After {formatCurrency(rebateAmount)} STC rebate)
                          </small>
                        </div>
                      ) : (
                        <span className="total-amount">{formatCurrency(order.ORDTOTAL)}</span>
                      )}
                    </span>
                  </div>
                </div>

                <div className="card-actions">
                  <Link to={"/order/" + order.SEQNO} className="action view">
                    View Order Details
                  </Link>
                </div>
              </div>
            );
          })
        ) : (
          <div className="no-orders-card">
            <p>{query ? `No orders found for "${query}"` : "No order records found"}</p>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {pagination.totalPages > 1 && (
        <Row className="mt-4 justify-content-center">
          <Col xs="auto">
            <Pagination
              count={pagination.totalPages}
              page={pagination.currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              disabled={isLoading}
            />
          </Col>
        </Row>
      )}
    </Container>
  );
}